import type {
  OrderListResponseType,
  OrderV2Dto,
} from "@sellar-io/api-v2/types";
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useFirebaseUser } from "../../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../../useSellarCurrentBusiness.shared";
import { apiV2AssertResponse, apiV2Request } from "../../apiV2Request";
import { ServerStateKeys } from "../../ServerStateKeys";

export const useOrderDispatch = (id: number) => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrderV2Dto.OrderDispatchRequestType) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: `orders/v2/${id}/dispatch`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse(response)
            .data as OrderV2Dto.OrderDispatchResponseType,
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.Orders, id],
      });
      queryClient.invalidateQueries({
        queryKey: [user?.uid, business?.id, ServerStateKeys.OrderEvents, id],
      });
      queryClient.invalidateQueries({
        queryKey: [business?.id, ServerStateKeys.OrderComments],
      });
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: [business?.id, ServerStateKeys.OrderIntegrationValue, id],
        });
      }, 1000);

      queryClient.setQueriesData<InfiniteData<OrderListResponseType>>(
        {
          predicate: ({ queryKey }) => {
            return (
              queryKey[0] === user?.uid &&
              queryKey[1] === business?.id &&
              queryKey[2] === ServerStateKeys.Orders &&
              typeof queryKey[3] === "object"
            );
          },
        },
        (data) => {
          if (!data?.pages) return data;
          return {
            ...data,
            pages: data.pages.map((page) =>
              page.map((order) =>
                order.id === response.id ? response : order,
              ),
            ),
          };
        },
      );
    },
  });
};
